import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { CampoEditavel } from "@/components/finance/proposta/CampoEditavel";
import { financeApi } from "@/services/finance-api";
import type { PropostaDetail } from "@/types/finance";

interface CampoCodigoPropostaProps {
  proposta: PropostaDetail;
  disabled: boolean;
  destaque?: boolean;
  onSalvar: (codigo: string | null) => void;
}

/**
 * Código da proposta já completo: o rascunho nasce com ele, "SIGLA YYYYMMDD" e
 * "-2" quando o do dia já existe. É o único campo de código: a agência sem
 * sigla aprende a dela com o primeiro código digitado. Enquanto se digita, o servidor diz se outra
 * proposta ou OS do Omie já usa o código; um código indisponível não é gravado.
 */
export function CampoCodigoProposta({ proposta, disabled, destaque, onSalvar }: CampoCodigoPropostaProps) {
  const valorAtual = proposta.codigo ?? proposta.codigo_previsto ?? "";
  const [digitado, setDigitado] = useState<string | null>(null);
  const [conflito, setConflito] = useState<string | null>(null);

  useEffect(() => {
    const codigo = digitado?.trim().replace(/\s+/g, " ").toUpperCase();
    if (!codigo || codigo === valorAtual) {
      setConflito(null);
      return;
    }
    let cancelado = false;
    const espera = setTimeout(() => {
      financeApi
        .verificarCodigoDaProposta(proposta.id, codigo)
        .then((resposta) => {
          if (!cancelado) setConflito(resposta.disponivel ? null : resposta.mensagem);
        })
        .catch(() => {
          if (!cancelado) setConflito(null);
        });
    }, 400);
    return () => {
      cancelado = true;
      clearTimeout(espera);
    };
  }, [digitado, valorAtual, proposta.id]);

  const bloqueado = proposta.os_enviada_ao_omie;

  return (
    <CampoEditavel
      id="campo-codigo"
      label="Código da proposta"
      valor={valorAtual}
      disabled={disabled || bloqueado}
      placeholder="Ex.: MTO 20261028"
      destaque={destaque}
      normalizar={(valor) => valor.toUpperCase()}
      onRascunhoChange={setDigitado}
      inputClassName={conflito ? "border-destructive focus-visible:ring-destructive" : undefined}
      ajuda={
        bloqueado ? (
          <span className="flex items-start gap-1 text-amber-700">
            <Lock className="mt-px h-3 w-3 shrink-0" />
            Não dá mais para alterar: uma OS desta proposta já está no Omie com este código (ou o envio ficou sem resposta)
          </span>
        ) : conflito ? (
          <span className="text-destructive">{conflito}</span>
        ) : proposta.codigo || proposta.codigo_previsto ? (
          "Ao trocar, gere o documento de novo. Vazio volta ao automático"
        ) : (
          "A agência ainda não tem sigla: digite o código e ela passa a valer nas próximas"
        )
      }
      onSalvar={(valor) => {
        if (conflito) return;
        onSalvar(valor || null);
      }}
    />
  );
}
